import { useCallback, useMemo, useRef } from 'react';
import useSWR, { type KeyedMutator, type SWRConfiguration } from 'swr';

import { getApiClient } from '@apps/web/lib/api_client';
import { isTransparentRateLimitError } from '@apps/web/lib/known_api_errors';
import { useMode } from '@apps/web/lib/mode_store';
import {
    type ApiBodyType,
    type ApiError,
    type ApiPathParamsType,
    type ApiQueryParamsType,
    type ApiResponseType,
    type Mode,
} from '@libraries/typescript/api_client';
import { type paths } from '@libraries/typescript/api_client/generated_api_types';
import { assert } from '@libraries/typescript/utilities';

type RestMethod = 'get' | 'post' | 'patch' | 'put' | 'delete';

type ApiQueryOptions<
    Path extends keyof paths,
    Method extends keyof paths[Path] & RestMethod,
    TSource = ApiResponseType<Path, Method>,
    TData = unknown,
> = {
    readonly path: Path;
    readonly method: Method;
    readonly select: (data: TSource) => TData;
    readonly enabled?: boolean;
    readonly pathParams?: ApiPathParamsType<Path>;
    readonly queryParams?: ApiQueryParamsType<Path, Method>;
    readonly body?: ApiBodyType<Path, Method>;
    readonly mode?: Mode;
    readonly reason?: string;
    // Opt a POST read into the api client's retries and request timeout, which
    // it otherwise applies only to GET/HEAD so a retry can never double-submit
    // a mutation. Set this only when the endpoint is verified side-effect free:
    // a read routed through POST to carry a body too complex for a query
    // string. Without it a single network blip surfaces to the user as
    // `TypeError: Failed to fetch` instead of being retried away.
    readonly idempotent?: boolean;
} & Pick<
    SWRConfiguration<TSource, ApiError>,
    | 'shouldRetryOnError'
    | 'errorRetryInterval'
    | 'errorRetryCount'
    | 'refreshInterval'
    | 'keepPreviousData'
    | 'revalidateOnFocus'
    | 'revalidateOnReconnect'
    | 'revalidateIfStale'
    | 'dedupingInterval'
    | 'refreshWhenHidden'
    | 'fallbackData'
    | 'revalidateOnMount'
>;

type ApiQueryResult<TSource, TData> = {
    readonly data: TData | undefined;
    readonly error: ApiError | undefined;
    readonly hasRequestFailed: boolean;
    readonly isLoading: boolean;
    readonly isValidating: boolean;
    readonly mutate: KeyedMutator<TSource>;
};

type ApiQueryResultWithRevalidation<TSource, TData> = ApiQueryResult<
    TSource,
    TData
> & {
    /** Revalidates like `mutate()`, but rejects when the request fails. */
    readonly revalidate: () => Promise<TSource | undefined>;
};

export function useApiQuery<
    Path extends keyof paths,
    Method extends keyof paths[Path] & RestMethod = 'get',
    TSource = ApiResponseType<Path, Method>,
    TData = ReturnType<ApiQueryOptions<Path, Method, TSource>['select']>,
>(
    options: ApiQueryOptions<Path, Method, TSource, TData> & {
        readonly withRejectingRevalidation: true;
    },
): ApiQueryResultWithRevalidation<TSource, TData>;
export function useApiQuery<
    Path extends keyof paths,
    Method extends keyof paths[Path] & RestMethod = 'get',
    TSource = ApiResponseType<Path, Method>,
    TData = ReturnType<ApiQueryOptions<Path, Method, TSource>['select']>,
>(
    options: ApiQueryOptions<Path, Method, TSource, TData>,
): ApiQueryResult<TSource, TData>;
export function useApiQuery<
    Path extends keyof paths,
    Method extends keyof paths[Path] & RestMethod = 'get',
    TSource = ApiResponseType<Path, Method>,
    TData = ReturnType<ApiQueryOptions<Path, Method, TSource>['select']>,
>({
    path,
    method,
    select,
    enabled = true,
    pathParams,
    queryParams,
    body,
    mode: requestedMode,
    reason,
    idempotent,
    withRejectingRevalidation,
    ...opts
}: ApiQueryOptions<Path, Method, TSource, TData> & {
    readonly withRejectingRevalidation?: true;
}):
    | ApiQueryResult<TSource, TData>
    | ApiQueryResultWithRevalidation<TSource, TData> {
    const apiClient = getApiClient();
    const currentMode = useMode();
    const effectiveMode = requestedMode ?? currentMode;
    const latestRequestErrorRef = useRef<{ readonly error: unknown } | null>(
        null,
    );

    const key = useMemo<
        readonly [
            method: Method,
            path: Path,
            pathParams: ApiPathParamsType<Path> | undefined,
            queryParams: ApiQueryParamsType<Path, Method> | undefined,
            body: ApiBodyType<Path, Method> | undefined,
            mode: Mode | undefined,
        ]
    >(
        () => [method, path, pathParams, queryParams, body, effectiveMode],
        [
            method,
            path,
            pathParams,
            queryParams,
            // Avoid expanding every generated request-body variant while
            // typing React's unknown-valued dependency list.
            body as unknown,
            effectiveMode,
        ],
    );

    const queryFn = useCallback(async () => {
        latestRequestErrorRef.current = null;
        const requestParams = {
            url: path,
            pathParams,
            queryParams,
            body,
            // Send the same mode the SWR key is computed from (above), so a
            // `ModeOverrideProvider` (e.g. a hard-pinned-live deep-link tab) is
            // honored by the request, not just the cache key. `effectiveMode`
            // is `requestedMode ?? useMode()`, so with no override the X-Mode
            // header value is unchanged from before (the store mode).
            mode: effectiveMode,
            reason,
        };

        try {
            let response: TSource;
            switch (method) {
                case 'get':
                    response = await apiClient.get(
                        requestParams as Parameters<typeof apiClient.get>[0],
                    );
                    break;
                case 'post':
                    response = await apiClient.post({
                        ...(requestParams as Parameters<
                            typeof apiClient.post
                        >[0]),
                        idempotent,
                        rateLimitFailureMode: idempotent ? 'silent' : undefined,
                    });
                    break;
                case 'patch':
                    response = await apiClient.patch(
                        requestParams as Parameters<typeof apiClient.patch>[0],
                    );
                    break;
                case 'put':
                    response = await apiClient.put(
                        requestParams as Parameters<typeof apiClient.put>[0],
                    );
                    break;
                case 'delete':
                    response = await apiClient.delete(
                        requestParams as Parameters<typeof apiClient.delete>[0],
                    );
                    break;
                default: {
                    assert.never(method);
                }
            }

            latestRequestErrorRef.current = null;
            return response;
        } catch (error) {
            latestRequestErrorRef.current = { error };
            throw error;
        }
    }, [
        apiClient,
        path,
        method,
        pathParams,
        queryParams,
        body,
        effectiveMode,
        reason,
        idempotent,
    ]);

    const swrOptions: SWRConfiguration<TSource, ApiError> = {
        shouldRetryOnError: false,
        ...opts,
    };

    const {
        data: source,
        error,
        isLoading,
        isValidating,
        mutate,
    } = useSWR<TSource, ApiError>(enabled ? key : null, queryFn, swrOptions);
    const revalidate = useCallback(async () => {
        const refreshed = await mutate();
        // SWR records revalidation failures in cache state but resolves its
        // bound mutator with retained data. The fetcher records that otherwise
        // hidden outcome so coordination code can release work on failure.
        if (latestRequestErrorRef.current !== null) {
            throw latestRequestErrorRef.current.error;
        }
        return refreshed;
    }, [mutate]);

    const data = useMemo(
        () => (source !== undefined ? select(source) : undefined),
        [source, select],
    );

    const result: ApiQueryResult<TSource, TData> = {
        data,
        error: isTransparentRateLimitError(error) ? undefined : error,
        // `error: undefined` alone cannot distinguish an intentionally
        // unpresented failure from no failure.
        hasRequestFailed: error !== undefined,
        isLoading,
        isValidating,
        mutate: mutate as unknown as KeyedMutator<TSource>,
    };
    return withRejectingRevalidation ? { ...result, revalidate } : result;
}
